import React, { useState } from 'react';
import getTextColor, { getPlaceholderColor } from '../util/Color';
import { useCallCenter } from '../providers/CallCenterProvider';
import { isValidEmail } from '../util/validation';
import Button from './Button';
import Modal from './Modal';

interface TopBarProps {
    setModalOpen: (value: boolean) => void;
}

interface FeedbackState {
    type: 'success' | 'warning';
    message: string;
}

const DEFAULT_SOURCE_OPTIONS = ['Walk-in', 'Phone call'];

// Used only before a location's corp config has loaded — there's no
// themeColor to draw from yet at that point.
const NEUTRAL_BAR_COLOR = '#374151';

const TopBar: React.FC<TopBarProps> = ({ setModalOpen }) => {
    /** Hooks */
    const {
        configuration,
        sortedLocations,
        configError,
        selectedLocationCode,
        agentName,
        agentComments,
        setAgentName,
        setAgentComments,
        setAgentDetails,
        clearAgentDetails,
        startNewEnquiry,
    } = useCallCenter();
    const [feedback, setFeedback] = useState<FeedbackState | null>(null);

    /** Constants */
    const locationCode = selectedLocationCode;
    const themeColor = configuration?.appConfig.journey.themeColor ?? NEUTRAL_BAR_COLOR;
    const textColor = getTextColor(themeColor, '#fff');
    const placeholderColor = getPlaceholderColor(themeColor);
    // `::placeholder` can't be targeted via the `style` prop directly — set
    // the color through a CSS variable instead, referenced by a static
    // (Tailwind-JIT-detectable) placeholder-color utility class.
    const placeholderStyleVar = {
        '--placeholder-color': placeholderColor,
    } as React.CSSProperties;

    const handleSave = (): void => {
        const missing: string[] = [];
        if (!agentName.trim()) {
            missing.push(
                configuration?.agentFieldType === 'email' ? 'Agent Email' : 'Agent Name'
            );
        }
        if (!agentComments.trim()) {
            missing.push('Source');
        }

        if (missing.length > 0) {
            setFeedback({
                type: 'warning',
                message: `Please fill in: ${missing.join(', ')}.`,
            });
            return;
        }

        if (configuration?.agentFieldType === 'email' && !isValidEmail(agentName)) {
            setFeedback({
                type: 'warning',
                message: 'Agent Email must be a valid email address.',
            });
            return;
        }

        setAgentDetails();
        setFeedback({ type: 'success', message: 'Agent details saved.' });
    };

    const handleReset = (): void => {
        clearAgentDetails();
        setFeedback({ type: 'success', message: 'Agent details cleared.' });
    };

    return (
        <>
            <div
                className='flex flex-col md:flex-row gap-2 lg:gap-6 p-4 justify-between items-center border-b-4'
                style={{
                    borderBottomColor: themeColor,
                    backgroundColor: themeColor,
                }}
            >
                <div className='flex flex-col md:flex-row gap-2 lg:gap-6 items-center'>
                    <select
                        onChange={(e) => {
                            if (e.currentTarget.value !== 'Choose a Location') {
                                // Preserve existing query params (?corp=, ?staging=, etc.) —
                                // this is a full page navigation, so anything not carried
                                // forward here is lost on reload.
                                const params = new URLSearchParams(
                                    window.location.search
                                );
                                params.set('locationCode', e.currentTarget.value);
                                // No corp config (and so no baseUrl) yet if a location
                                // hasn't been chosen/resolved — stay on the current path.
                                window.location.href = `${
                                    configuration?.baseUrl ?? window.location.pathname
                                }?${params.toString()}`;
                            }
                        }}
                        className='rounded-lg p-2 font-semibold border-none'
                        style={{
                            backgroundColor: themeColor,
                            color: textColor,
                        }}
                        value={locationCode || ''}
                    >
                        <option value={undefined}>Choose a Location</option>
                        {sortedLocations.map((loc, index) => (
                            <option key={index} value={loc.code}>
                                {loc.name}
                            </option>
                        ))}
                    </select>

                    {configuration ? (
                        <>
                            <input
                                type={
                                    configuration.agentFieldType === 'email'
                                        ? 'email'
                                        : 'text'
                                }
                                placeholder={
                                    configuration.agentFieldType === 'email'
                                        ? 'Agent Email'
                                        : 'Agent Name'
                                }
                                className='bg-transparent px-1 py-2 font-semibold border-0 border-b-2 border-solid placeholder-[var(--placeholder-color)] focus:outline-none'
                                onChange={(e) => setAgentName(e.target.value)}
                                style={{
                                    borderBottomColor: textColor,
                                    color: textColor,
                                    ...placeholderStyleVar,
                                }}
                                value={agentName}
                            />

                            {configuration.sourceInputType === 'dropdown' ? (
                                <select
                                    className='bg-transparent px-1 py-2 font-semibold border-0 border-b-2 border-solid focus:outline-none'
                                    onChange={(e) => setAgentComments(e.target.value)}
                                    style={{
                                        borderBottomColor: textColor,
                                        color: textColor,
                                        backgroundColor: themeColor,
                                    }}
                                    value={agentComments}
                                >
                                    <option value=''>Source</option>
                                    {(configuration.sourceOptions?.length
                                        ? configuration.sourceOptions
                                        : DEFAULT_SOURCE_OPTIONS
                                    ).map((option) => (
                                        <option key={option} value={option}>
                                            {option}
                                        </option>
                                    ))}
                                </select>
                            ) : (
                                <input
                                    type='text'
                                    placeholder='Source (e.g. Walk-in, Phone call)'
                                    className='bg-transparent px-1 py-2 font-semibold border-0 border-b-2 border-solid placeholder-[var(--placeholder-color)] focus:outline-none'
                                    onChange={(e) => setAgentComments(e.target.value)}
                                    style={{
                                        borderBottomColor: textColor,
                                        color: textColor,
                                        ...placeholderStyleVar,
                                    }}
                                    value={agentComments}
                                />
                            )}

                            <Button corporation={configuration} onClick={handleSave}>
                                Save
                            </Button>
                            <Button corporation={configuration} onClick={handleReset}>
                                Reset
                            </Button>
                            <Button corporation={configuration} onClick={startNewEnquiry}>
                                New Enquiry
                            </Button>
                        </>
                    ) : (
                        <span className='font-semibold' style={{ color: textColor }}>
                            {configError ?? 'Select a location to continue'}
                        </span>
                    )}
                </div>

                {configuration && (
                    <div>
                        <span>
                            <svg
                                xmlns='http://www.w3.org/2000/svg'
                                fill='none'
                                viewBox='0 0 24 24'
                                strokeWidth={2}
                                className='size-8 mr-8 hover:cursor-pointer'
                                style={{
                                    stroke: configuration.secondaryColor,
                                }}
                                onClick={() => setModalOpen(true)}
                            >
                                <path
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                    d='m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z'
                                />
                            </svg>
                        </span>
                    </div>
                )}
            </div>

            {configuration && (
                <Modal show={feedback !== null}>
                    <div className='flex flex-col items-center gap-3 text-center'>
                        {feedback?.type === 'warning' ? (
                            <svg
                                xmlns='http://www.w3.org/2000/svg'
                                fill='none'
                                viewBox='0 0 24 24'
                                strokeWidth={2}
                                stroke='currentColor'
                                className='size-10 text-amber-500'
                            >
                                <path
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                    d='M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z'
                                />
                            </svg>
                        ) : (
                            <svg
                                xmlns='http://www.w3.org/2000/svg'
                                fill='none'
                                viewBox='0 0 24 24'
                                strokeWidth={2}
                                stroke='currentColor'
                                className='size-10 text-green-500'
                            >
                                <path
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                    d='M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z'
                                />
                            </svg>
                        )}
                        <p className='text-lg font-semibold'>{feedback?.message}</p>
                        <Button
                            corporation={configuration}
                            onClick={() => setFeedback(null)}
                        >
                            OK
                        </Button>
                    </div>
                </Modal>
            )}
        </>
    );
};

export default TopBar;
